import { inject, Injector } from '@angular/core';
import { RootStore } from '@sama/app-state';
import { mapAsKeys } from '@sama/js-core';
import { isEmpty, isEqual } from '@sama/micro-dash';
import { AbstractContentConnector } from '../../cross-team-shared/abstract-content-connector';
import { AnnotationTransientState } from '../../cross-team-shared/annotation/shared/state/annotation-state';
import { ShapeLocationState2D } from '../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState2D } from '../../cross-team-shared/annotation/shared/state/shape-state';
import {
  EventTrackingValueWithSource,
  selectionToArray,
} from '../../cross-team-shared/misc-utils';
import { ShapeFilter } from '../../cross-team-shared/state/abstract-sensor-content-state';
import { ToolName } from '../../cross-team-shared/tools-names';
import {
  midPoint,
  subtractPoints,
} from '../../shared/misc-utilities/point-utils';
import { delayExecution } from '../../shared/misc-utilities/promise-utils';
import {
  ContextMenuOption,
  MenuType,
  ShapeGroupOrderState,
} from '../../team-apis/content/content-connector';
import { ViewBox } from '../../team-apis/content/view-box';
import { EventTrackingValue } from '../../team-apis/core/event-tracking';
import { IN_PROGRESS_INDEX } from '../../team-apis/core/reserved-shape-indexes';
import { shellConnectorToken } from '../../team-apis/core/shell-connector';
import {
  Point2D,
  VectorTaggingShapeType,
} from '../../team-apis/otterdam/answers/shape-typings';
import { Task } from '../../team-apis/otterdam/task';
import { AbstractAssetInitializationService } from './asset-loading/abstract-asset-initialization.service';
import { ContextMenuOptionsService } from './context-menu-options.service';
import { getShapeHelper } from './shape-helpers/get-shape-helper';
import {
  AbstractContentUndoService2D,
  UndoState2D,
} from './state/abstract-content-state-2d-undo.service';
import { AbstractSensorContentState2D } from './state/abstract-sensor-content-state-2d';
import { AbstractSensorContentStore2D } from './state/abstract-sensor-content-store-2d';
import { InspectService } from './toolbars/inspect.service';
import { ShapeTranslationHelper } from './tools/edit/shape-translation-helper';
import { ToolManager2DService } from './tools/shared/tool-manager-2d.service';
import { ExtrapolationService } from './utils/extrapolation/extrapolation.service';
import { ensureKeyLocation, LocationHelper } from './utils/location-helper';
import { ShapeValidationService } from './utils/shape-validation.service';
import { ViewBoxService } from './utils/view-box.service';

export abstract class AbstractContentConnector2D extends AbstractContentConnector<
  Point2D,
  VectorTaggingShapeType,
  ShapeLocationState2D
> {
  protected store = inject(AbstractSensorContentStore2D);
  protected viewBoxService = inject(ViewBoxService);

  private contextMenuOptionsService!: ContextMenuOptionsService;
  private inspectService = inject(InspectService);
  private shapeValidationService = inject(ShapeValidationService);
  private extrapolationService = inject(ExtrapolationService);
  private shellConnector = inject(shellConnectorToken);
  private toolManagerService = inject(ToolManager2DService);
  private injector = inject(Injector);

  protected abstract assetInitializationService: AbstractAssetInitializationService;
  protected abstract undoService: AbstractContentUndoService2D<
    AbstractSensorContentState2D,
    UndoState2D
  >;

  constructor() {
    super();
    // Team split hack: some dependencies of the context menu service are still
    // injecting the core team's undo service. If we inject it during
    // initialization like the other services we get from the gangline it causes
    // timing issues.
    void delayExecution(() => {
      this.contextMenuOptionsService = this.injector.get(
        ContextMenuOptionsService,
      );
    });
  }

  deactivateSelectedTool() {
    const selectedTool = this.toolManagerService.getSelectedTool();
    if (selectedTool?.activated$.value) {
      selectedTool.onDeactivate();
    }
  }

  async activateSelectedTool() {
    const selectedTool = this.toolManagerService.getSelectedTool();
    if (selectedTool && !selectedTool.activated$.value) {
      await this.toolManagerService.getSelectedTool()?.onActivate();
    }
  }

  redo() {
    this.undoService.redo();
  }

  reset() {
    this.undoService.reset();
  }

  undo() {
    this.undoService.undo();
  }

  setSelection(indexes: number[]) {
    const initialSelection = selectionToArray(this.store.state().selection);
    const filteredIndexes = indexes.filter(
      (index) =>
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- need to update type to strictRecord one day
        index === IN_PROGRESS_INDEX || this.store.state().shapes[index],
    );
    if (isEqual(filteredIndexes, initialSelection)) {
      return;
    }

    this.store('selection').set(
      mapAsKeys(
        filteredIndexes,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- need to update type to strictRecord one day
        (index) => this.store.state().selection[index] || {},
      ),
    );
  }

  override async inspectFocusedShape() {
    return this.inspectService.inspectFocusedShape();
  }

  determineLocation(frameNumber: number, shapeIndex: number) {
    const helper = new LocationHelper(this.store);
    return helper.determineLocation(
      this.store.state().shapes[shapeIndex].key_locations,
      frameNumber,
    );
  }

  ensureKeyLocation(
    shapeIndex: number,
    frameNumber: number,
    specifiedLocation?: ShapeLocationState2D,
  ) {
    ensureKeyLocation(
      new LocationHelper(this.store),
      this.store('shapes')(shapeIndex)('key_locations'),
      frameNumber,
      specifiedLocation,
    );
    this.shellConnector.setShapes(this.store.state());
  }

  inspectFeedbackMarker(position: Point2D) {
    return this.inspectService.panToCenterOfBoundingPoints([
      position,
      position,
    ]);
  }

  loadTask(task: Task) {
    this.store.assign({ task, taskExtras: task._extras });
    return this.assetInitializationService.loadTask();
  }

  resetSensorView(eventTrackingValueWithSource: EventTrackingValueWithSource) {
    this.viewBoxService.resetZoom(eventTrackingValueWithSource);
  }

  override setFrameNumber(frameNumber: number) {
    this.store('frameNumber').set(frameNumber);
  }

  override setFeedbackMarkers(points: Point2D[]) {
    this.store('vectorOverlay')('shapeFeedbackMarkers').set(points);
  }

  setShapeFilters(filters: ShapeFilter[]) {
    this.store('shapeFilters').set(filters);
  }

  setShapeTransientInfo(
    shapeTransientInfo: StrictRecord<number, AnnotationTransientState>,
  ) {
    this.store('shapeTransientInfo').set(shapeTransientInfo);
  }

  setShapes({
    shapes,
    groups,
    shapeAndGroupOrder,
  }: ShapeGroupOrderState<VectorTaggingShapeType, Point2D>) {
    this.store.batch(() => {
      // @ts-ignore -- fix me
      this.store('shapes').set(shapes);
      // @ts-ignore -- fix me
      this.store('groups').set(groups);
      this.store('shapeAndGroupOrder').set(shapeAndGroupOrder);
    });
  }

  showTrainingShapes(shapes: ShapeState2D[]) {
    this.store('trainingShapes').set(this.convertToTrainingShapes(shapes));
  }

  isOutputBlank() {
    return isEmpty(this.store.state().shapes);
  }

  wasShapeCancelled() {
    return this.store.state().wasShapeCreationCancelled;
  }

  getContextMenuOptions(menuType: MenuType): Promise<ContextMenuOption[]> {
    return this.contextMenuOptionsService.getContextMenuOptions(menuType);
  }

  validateShape(shape: ShapeState2D, isForSingleFrame = true) {
    return this.shapeValidationService.validate(shape, isForSingleFrame);
  }

  override updateExtrapolation(shapeIndex: number, startKeyFrame: number) {
    return this.extrapolationService.updateExtrapolation(
      shapeIndex,
      startKeyFrame,
    );
  }

  handleViewBoxUpdate(outputName: string, viewBox: ViewBox) {
    if (this.store.state().output.name === outputName) {
      this.store('viewBox').set(viewBox);
    }
  }

  handleZoomPanelViewBoxUpdate(_outputId: string, viewbox: ViewBox) {
    this.store('zoomPanelViewBox').set([
      viewbox[0] + 0.5,
      viewbox[1] + 0.5,
      viewbox[2],
      viewbox[3],
    ]);
  }

  override setIsZoomPanelOpen(isOpen: boolean) {
    this.store('isZoomPanelOpen').set(isOpen);
  }

  override pushCurrentState(
    options: { collectKey?: string; collectDebounce?: number } = {},
  ) {
    return this.undoService.pushCurrentState(options);
  }

  override setTool(toolName: ToolName, mode?: string) {
    this.store('selectedTool').set(toolName);

    if (mode) {
      this.toolManagerService.getTool(toolName).setMode(mode);
    }
  }

  override cancelTool(eventTrackingValue: EventTrackingValue) {
    this.toolManagerService.cancelTool(eventTrackingValue);
  }

  offsetShapesToMousePosition(shapes: ShapeState2D[]) {
    const shapeCenters = shapes.map((shape) => {
      const shapeHelper = getShapeHelper(shape.type);
      const points = shape.key_locations[0].points;
      return midPoint(shapeHelper.getBoundingPoints(points));
    });
    const centerOfAllShapes = midPoint(shapeCenters);

    const newCenter = this.store.state().mouse.imagePoint;

    const deltaBetweenCenters = subtractPoints(newCenter, centerOfAllShapes);

    return shapes.map((shape) => {
      const shapeStore = new RootStore(shape);
      const helper = new ShapeTranslationHelper(this.injector, {
        shapeStoreToMove: shapeStore,
      });
      helper.move(deltaBetweenCenters);
      return shapeStore.state();
    });
  }
}
